import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { callAPI, formatIndianCurrency, getLocalValue, LoacalVariables, Method, toBoolean } from "../../commonFunctions/CommonFunctions";
import WSButton from "../../Components/Button/WSButton";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { NotFound } from "../NotFound";
import logo from '../../images/logo.svg'
import React from "react";
import { IBranch } from "../DTO/Dto";
const OrderPrint = () => {
  const { id } = useParams();
  const { showMessage } = useSnackbar();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [branchData, setBranchData] = useState<IBranch>();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function getDataById() {
      try {
        const response: any = await callAPI("workorders", Method.GET, null, id);
        if (response?.data) {
          setData(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    }
    async function getBranchDetails() {
      try {
        const response: any = await callAPI("Branch", Method.GET, null, getLocalValue(LoacalVariables.Branch));
        if (response?.data) {
          setBranchData(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    }


    if (id) {
      getDataById();
      getBranchDetails();
    }
  }, [id]);

  // 🖨️ Print Handler
  const handlePrint = useReactToPrint({
    contentRef
  });
  const totalAmount = data?.quotationItems
    .reduce((sum: number, item: any) => sum + Number(item.quantity * item.unitPrice || 0), 0)
    .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (!data) return <NotFound />;

  const totalInluded = data.quotationItems
    .filter((item: any) => item.isIncluded)
    .reduce((sum: number, item: any) => sum + Number(item.unitPrice * item?.quantity), 0)
    .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const totalExcluded = data.quotationItems
    .filter((item: any) => !item.isIncluded)
    .reduce((sum: number, item: any) => sum + Number(item.unitPrice * item?.quantity), 0)
    .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  return (
    <div>
      <WSButton
        id="printBtn"
        label="Print Order"
        variant="contained"
        color="primary"
        onClick={handlePrint}
        size="small"
        sx={{ marginBottom: 2 }}
      />
      <Paper
        ref={contentRef}
        style={{ padding: 20, maxWidth: 600, margin: "auto" }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 1
          }}
        >  <Box ml={2} display={'flex'} justifyContent={'flex-end'}>
            <img src={logo} alt="Company Logo" style={{ objectFit: "contain" }} />
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h5" fontWeight="bold" color="error" fontStyle={'italic'}>
              {branchData?.name}
            </Typography>
            <Typography sx={{ fontWeight: "bold", fontSize: '0.875rem' }}>
              {branchData?.address?.addressLine1} P.O {branchData?.address?.pincode}
            </Typography>
            {branchData?.address?.addressLine2 && <Typography sx={{ fontWeight: "bold", fontSize: '0.875rem' }}>
              {branchData?.address?.addressLine2}
            </Typography>}
          </Box>

          {/* Contact Details */}
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
              📞 {branchData?.phoneNumber}
            </Typography>
            {branchData?.alternatePhoneNumber && <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
              📞  {branchData?.alternatePhoneNumber}
            </Typography>}
            {/* <Typography sx={{ fontWeight: "bold", fontSize: 14 }}>
              📞 9544 541 117
            </Typography> */}
          </Box>
        </Box>

        <Box sx={{ borderBottom: "1px solid black", marginBottom: 2 }}></Box>

        {/* Order Form Title */}
        <Box sx={{ textAlign: "center", marginBottom: 2 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: '1rem' }}>
            ORDER FORM
          </Typography>
          <Typography sx={{ fontSize: '0.875rem' }}>
            Date: {dayjs(data?.date).format("DD/MM/YYYY")}
          </Typography>
        </Box>

        {/* Client & Order Details */}
        <TableContainer>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.75rem' }}>
                  Name of Client
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{data?.customerName || "N/A"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.75rem' }}>Address</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{data?.address || "N/A"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.75rem' }}>Phone</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{data?.phone || "N/A"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.75rem' }}>
                  Approximate Area
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{data?.approximateArea || "N/A"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.75rem' }}>Estimation Amount</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{Number(data?.rate).toFixed(2) || 0}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.75rem' }}>
                  Date of Commencement
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>
                  {dayjs(data?.commencement).format("DD/MM/YYYY")}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: '0.75rem' }}>
                  Date of Completion
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>
                  {dayjs(data?.completion).format("DD/MM/YYYY")}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>


        {/* Table */}
        <TableContainer component={Paper} sx={{ mt: 1 }}>
          <Table size="small" >
            <TableHead>
              {/* Header Row */}
              <TableRow sx={{ bgcolor: 'success.main' }}>
                <TableCell sx={{ fontWeight: 700, color: 'white' }}>
                  <Typography sx={{ fontSize: '0.75rem' }} fontWeight={700}>
                    REQUIRED ITEMS
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{ bgcolor: 'success.main', fontWeight: 700, color: 'white', textAlign: 'center' }}
                  colSpan={3}
                >
                  <Typography sx={{ fontSize: '0.75rem' }} fontWeight={700}>
                    WORK INCLUDED
                  </Typography>
                </TableCell>
              </TableRow>

              {/* Sub Header Row */}
              <TableRow sx={{ bgcolor: 'success.light' }}>
                <TableCell>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                    PARTICULAR
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                    QTY/Sqft
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                    RATE
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                    AMOUNT
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.quotationItems
                .filter((item: any) => item.isIncluded)
                .map((item: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{item.productName}{' '}{item.isPolish !== '2' && (
                      <>({toBoolean(item.isPolish) ? 'With Polish' : 'Without Polish'})</>
                    )} ({item?.unit})</TableCell>
                    <TableCell align="center"><Typography sx={{ fontSize: '0.75rem' }} >{item.quantity}</Typography></TableCell>
                    <TableCell align="center"><Typography sx={{ fontSize: '0.75rem' }} > {Number(item.unitPrice).toFixed(2)}</Typography></TableCell>
                    <TableCell align="center"><Typography sx={{ fontSize: '0.75rem' }} >  {Number(item.unitPrice * item.quantity).toFixed(2)}</Typography></TableCell>
                  </TableRow>
                ))}

              {/* TOTAL row */}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}><Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: 'success.main' }} >TOTAL</Typography></TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  {/* <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }} >{data.quotationItems
                    .filter((item: any) => item.isIncluded)
                    .reduce((sum: number, item: any) => sum + Number(item.quantity), 0)}</Typography> */}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  {/* <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }} >{data.quotationItems
                    .filter((item: any) => item.isIncluded)
                    .reduce((sum: number, item: any) => sum + Number(item.unitPrice), 0)
                    .toLocaleString()}</Typography> */}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: 'success.main' }} >{totalInluded
                    ? formatIndianCurrency(totalInluded)
                    : '0.00'}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {data.quotationItems
          .filter((item: any) => !item.isIncluded).length > 0 && <TableContainer component={Paper} sx={{ marginTop: 2 }}>
            <Table size="small" >
              <TableHead>
                {/* Main Header */}
                <TableRow sx={{ bgcolor: 'error.main' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>
                    <Typography sx={{ fontSize: '0.75rem' }} fontWeight={700}>
                      REQUIRED ITEMS
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ bgcolor: 'error.main', fontWeight: 700, color: 'white', textAlign: 'center' }}
                    colSpan={3}
                  >
                    <Typography sx={{ fontSize: '0.75rem' }} fontWeight={700}>
                      WORK EXCLUDED
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Sub Header */}
                <TableRow sx={{ bgcolor: 'error.light' }}>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                      PARTICULAR
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                      QTY/Sqft
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                      RATE
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                      AMOUNT
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.quotationItems
                  .filter((item: any) => !item.isIncluded)
                  .map((item: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{item.productName} ({item?.unit})</TableCell>
                      <TableCell align="center"><Typography sx={{ fontSize: '0.75rem' }} >{item.quantity}</Typography></TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontSize: '0.75rem' }}> {Number(item.unitPrice).toFixed(2)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontSize: '0.75rem' }}> {Number(item.unitPrice * item.quantity).toFixed(2)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}

                {/* TOTAL row */}
                <TableRow sx={{ bgcolor: 'error.lighter' }}>
                  <TableCell sx={{ fontWeight: 700 }}><Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: 'error.main' }} >TOTAL</Typography></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {/* <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }} >{data.quotationItems
                    .filter((item: any) => !item.isIncluded)
                    .reduce((sum: number, item: any) => sum + Number(item.quantity), 0)}</Typography> */}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {/* <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }} >{data.quotationItems
                    .filter((item: any) => !item.isIncluded)
                    .reduce((sum: number, item: any) => sum + Number(item.unitPrice), 0)
                    .toLocaleString()}</Typography> */}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: 'error.main' }} >{totalExcluded
                      ? formatIndianCurrency(totalExcluded)
                      : '0.00'}</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        }
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3, mt: 1 }}>
          <Table size="small">
            <TableHead>

              <TableRow sx={{ bgcolor: '#00A300' }}>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} colSpan={4}>
                  <Typography sx={{ fontSize: '0.75rem' }} fontWeight={700}>
                    Total Order
                  </Typography>
                </TableCell>

              </TableRow>
              <TableCell sx={{ fontWeight: 700, color: 'green', textAlign: 'right', paddingRight: 7 }} >{totalAmount
                ? formatIndianCurrency(totalAmount)
                : '0.00'}</TableCell>

            </TableHead>

            <TableBody>


            </TableBody>
          </Table>
        </TableContainer>
        {/* Terms and Condtion */}

        {data.termsAndConditions !== "" && <Box mt={1}>
          <Typography sx={{ fontWeight: 700, mb: 1, fontSize: '0.75rem' }}>
            Terms and Conditions.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              pl: 2,
              fontSize: '0.75rem',
              whiteSpace: 'pre-line' // 🔹 respects \n as line breaks
            }}
          >
            {data.termsAndConditions}
          </Typography>
        </Box>}


        {/* Payment Terms */}
        {/* {data?.paymentTerms === "" && <Box mt={1}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '0.75rem' }} >
            Mode of Payment:
          </Typography>
          <ul>
            <li>
              <Typography sx={{ fontSize: '0.75rem' }}>
                <b>30%</b> of total value of work on order confirmation.
              </Typography>
            </li>
            <li>
              <Typography sx={{ fontSize: '0.75rem' }}>
                <b>60%</b> of total value of work on delivery of material to
                site.
              </Typography>
            </li>
            <li>
              <Typography sx={{ fontSize: '0.75rem' }}>
                <b>10%</b> balance after completion of work.
              </Typography>
            </li>
          </ul>
        </Box>} */}

        {data?.paymentTerms?.length > 0 && (
          <Box
            mt={1}
            // This prevents splitting across pages
            sx={{
              breakInside: 'avoid',         // modern
              pageBreakInside: 'avoid',     // legacy
              WebkitColumnBreakInside: 'avoid' // Safari
            }}
          >
            <Typography
              sx={{ fontWeight: 700, mb: 1, fontSize: '0.75rem' }}
            >
              Mode of Payment.
            </Typography>

            {/* Remarks */}
            {data?.paymentTerms?.map((item: any, idx: number) => (
              <Typography
                key={idx}
                variant="h6"
                sx={{ marginTop: 0, fontSize: '0.75rem', pl: 2 }}
              >
                <strong>{item?.percentage}%</strong> {item?.description}
              </Typography>
            ))}
          </Box>
        )}

        <Typography variant="h6" sx={{ marginTop: 0, fontWeight: "bold", fontSize: '0.75rem' }}>
          Remarks
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.75rem', pl: 2 }}>{data?.remarks || "N/A"}</Typography>

        {/* Footer */}
        <Divider sx={{ my: 3 }} />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Box>
            <Typography fontWeight="bold" sx={{ fontSize: '0.75rem' }}>Accepted:</Typography>
            <Typography sx={{ fontSize: '0.75rem' }}>Name of Client: ___________________</Typography>
            <Typography sx={{ fontSize: '0.75rem' }}>Witnesses:</Typography>
            <Typography sx={{ fontSize: '0.75rem' }}>1. ___________________</Typography>
            <Typography sx={{ fontSize: '0.75rem' }}>2. ___________________</Typography>
          </Box>
          <Box textAlign="right">
            <Typography sx={{ fontSize: '0.75rem' }}>Thanks & Regards,</Typography>
            <Typography fontWeight="bold" sx={{ fontSize: '0.75rem' }}>JOSEPH PJ</Typography>
            <Typography sx={{ fontSize: '0.75rem' }}>Managing Director</Typography>
          </Box>
        </Box>
      </Paper>
    </div>
  );
};

export default OrderPrint;
